import {OfferNode} from '../types'

/**
 * The deepest level that starts expanded. Levels 0–2 reveal their children, so the tree loads
 * showing four tiers (0–3) with the level-3 nodes collapsed — i.e. everything *beyond* level 2
 * starts collapsed (AC), keeping a deep network's initial render small and fast (PT-5721).
 */
export const MAX_EXPANDED_LEVEL = 2

/**
 * Stable per-node key for expansion state. Uses the offer id — the same identity the KPI panels
 * and scroll anchors key off — so an expansion override, a scroll target and a KPI panel all
 * refer to the node by one consistent string.
 *
 * Production offers always carry a numeric id; the theoretical id-less offer keys to "undefined"
 * (as it already does for its scroll anchor and KPI panel across the tree) and is never a reveal
 * target, so this consistent-with-the-rest limitation is accepted rather than worked around.
 */
export const nodeKey = (node: OfferNode): string => String(node.offer.id)

/**
 * Whether a node at the given depth starts expanded before the user touches it. Levels up to and
 * including {@link MAX_EXPANDED_LEVEL} do, so anything beyond level 2 loads collapsed.
 */
export const isExpandedByDefault = (level: number): boolean => level <= MAX_EXPANDED_LEVEL

/**
 * The keys of every ancestor of `node` (parent, grandparent, …), nearest first; empty for a root.
 *
 * A node is only mounted when all of its ancestors are expanded, so revealing a node deep in a
 * collapsed branch means expanding exactly these keys before scrolling to it.
 */
export const ancestorKeys = (node: OfferNode): string[] => {
  const keys: string[] = []
  let current = node.parent
  while (current) {
    keys.push(nodeKey(current))
    current = current.parent
  }
  return keys
}
